import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { TFile } from 'types/user';
import { getFilesById, getOptionFile } from 'services/files';
import { useAsyncCallback } from 'hooks/useAsyncCallback';
import { useNavigate, useParams } from 'react-router-dom';
import { payloadLoadCreateReport, createSeizureReport } from 'services/leads';
import { compact, uniqBy } from 'lodash';
import axios from 'axios';
import { TypeOption } from 'app/pages/Admin/NewFileSetUpPage/hook';
import { StorageServices } from 'services/storage';
import { createFileNote, CreateFileNotePayload } from 'services/fileNote';

export interface FormDataSeizure {
  kilometers: number;
  hasKey: boolean;
  run: boolean;
  signedVsa: boolean;
  possibleRedemption: boolean;
  conditionReport: string;
  towingCompany: string;
  transportRequired: boolean;
  billsIncurred: boolean;
  otherAddress?: string;
  fileType?: string;
  otherCustomerPOR?: string;
  addressLeadId: string;
  customersLeadId: string;
  removedToId: number;
  removedToOther?: string;
  otherBills?: string;
  otherTransport?: string;
}
const storage = new StorageServices();

export const useSeizure = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [photos, setPhotos] = useState<File[]>([]);
  const [file, setFile] = useState<TFile>();
  const [vsaDocuments, setVsaDocuments] = useState<File[]>([]);
  const fileInputPhotoRef = useRef<HTMLInputElement | null>(null);
  const fileInputDocumentRef = useRef<HTMLInputElement | null>(null);
  const [optionAuctionDeliveryLocation, setOptionAuctionDeliveryLocation] = useState<TypeOption[]>(
    []
  );
  const handleFileChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    type: 'photo' | 'document',
    isChange?: boolean
  ) => {
    const fileList = event.target.files;
    if (!fileList) return;
    const newFiles = Array.from(fileList);

    if (type === 'photo') {
      setPhotos((prevFiles) => uniqBy([...prevFiles, ...newFiles], 'name'));
      return;
    }
    if (isChange) {
      setVsaDocuments(uniqBy([...newFiles], 'name'));
    } else {
      setVsaDocuments((prevFiles) => uniqBy([...prevFiles, ...newFiles], 'name'));
    }
  };

  const handleDeleteDocument = (indexToDelete: number) => {
    // @ts-ignore
    fileInputDocumentRef.current.value = '';
    setVsaDocuments((prevFiles) => prevFiles.filter((_, index) => index !== indexToDelete));
  };

  const getFiles = useCallback(async () => {
    const [dataFile, dataOption] = await Promise.all([getFilesById(Number(id)), getOptionFile()]);
    const { resOptionAuctionDeliveryLocation } = dataOption;
    setOptionAuctionDeliveryLocation([
      // eslint-disable-next-line no-unsafe-optional-chaining
      ...resOptionAuctionDeliveryLocation?.auctionHouses?.map(
        (item: { id: number; value: string }) => ({
          value: item.id,
          label: item.value
        })
      ),
      { value: 0, label: 'Other location' }
    ]);
    const fileRes = dataFile.file as TFile;
    setFile(fileRes);
  }, [id]);

  const { asyncCallback: asyncGetFiles } = useAsyncCallback(getFiles, []);

  useEffect(() => {
    asyncGetFiles();
  }, [id]);

  const optionsAddress = useMemo(() => {
    const option = file?.leads.map((item) => {
      if (item.address) {
        return item;
      }
      return;
    });
    return [...compact(option)];
  }, [file]);

  const optionsPostalCode = useMemo(() => {
    const option = file?.leads.map((item) => {
      if (!item.postalCode) return;
      return {
        value: `${item.postalCode}`,
        label: `${item.postalCode}`
      };
    });
    return uniqBy([...compact(option), { value: 'other', label: 'Other Postal Code' }], 'value');
  }, [file]);
  const onSubmitSeizure = useCallback(
    async (data: FormDataSeizure) => {
      if (data.signedVsa && !vsaDocuments?.length) return;
      const documentFormData = new FormData();
      const photoFormData = new FormData();
      const filePhotosIds: number[] = [];
      const fileDocumentsIds: number[] = [];
      if (file?.fileNumber) {
        photoFormData.append('fileNumber', file?.fileNumber);
      }
      if (photos?.length) {
        for (const photo of photos) {
          photoFormData.append('files', photo, photo.name);
        }
        const { data: resListIdFile } = await axios.post(
          '/storages/pdf-combine/seizure-report-photo',
          photoFormData,
          {
            headers: { 'Content-Type': 'multipart/form-data' }
          }
        );
        filePhotosIds.push(resListIdFile.pdfId);
      }

      if (vsaDocuments?.length) {
        for (const document of vsaDocuments) {
          documentFormData.append('files', document, document.name);
        }
        if (data.fileType === 'IMAGE') {
          const { data: resListIdFile } = await axios.post(
            '/storages/pdf-combine/vsa-document',
            documentFormData,
            {
              headers: { 'Content-Type': 'multipart/form-data' }
            }
          );
          fileDocumentsIds.push(resListIdFile.pdfId);
        } else {
          const { data: resListIdFile } = await axios.post(
            '/storages/vsa-document',
            documentFormData,
            {
              headers: { 'Content-Type': 'multipart/form-data' }
            }
          );
          fileDocumentsIds.push(resListIdFile.files[0]);
        }
      }

      const otherAddress = data.addressLeadId === 'other';
      const otherCustomer = data.customersLeadId === 'other';

      const payload: payloadLoadCreateReport = {
        fileId: Number(id),
        kilometers: data.kilometers,
        hasKey: data.hasKey,
        addressLeadId: !otherAddress ? Number(data.addressLeadId) : undefined,
        otherAddress: otherAddress ? data.otherAddress : undefined,
        customersLeadId: !otherCustomer ? Number(data.customersLeadId) : undefined,
        otherCustomerPOR: otherCustomer ? data.otherCustomerPOR : undefined,
        run: data.run,
        signedVsa: data.signedVsa,
        possibleRedemption: data.possibleRedemption,
        transportRequired: data.transportRequired,
        billsIncurred: data.billsIncurred,
        conditionReport: data.conditionReport,
        towingCompany: data.towingCompany,
        vsaDocumentIds: fileDocumentsIds,
        seizureReportPhotoIds: filePhotosIds,
        removedToId: data.removedToId || undefined,
        removedToOther: data.removedToId === 0 ? data.removedToOther : undefined,
        otherBills: data.otherBills,
        otherTransport: data.otherTransport
      };
      const fileNote: CreateFileNotePayload = {
        fileId: Number(id),
        content: 'Seizure Report Submitted'
      };
      await Promise.all([createSeizureReport(payload), createFileNote(fileNote)]);
      if (storage.getRole() === 'USER') {
        navigate('/');
      } else {
        navigate('/admin');
      }
    },
    [file?.fileNumber, id, navigate, photos, vsaDocuments]
  );

  const { asyncCallback: asyncUpdateSeizure } = useAsyncCallback(onSubmitSeizure, []);

  const handleBack = () => navigate(-1);

  return {
    file,
    setVsaDocuments,
    photos,
    vsaDocuments,
    fileInputPhotoRef,
    fileInputDocumentRef,
    handleFileChange,
    handleDeleteDocument,
    handleSubmit: asyncUpdateSeizure,
    optionsAddress,
    handleBack,
    optionAuctionDeliveryLocation,
    optionsPostalCode
  };
};
